<template>
  <v-dialog
    v-model="dialog"
    scrollable
    :max-width="$vuetify.breakpoint.smAndUp ? '800' : '90%'"
    @keydown.esc="dialog = false"
  >
    <template v-slot:activator="{ on, attrs }">
      <!-- show a button if no rx has been provided-->
      <div class="">
        <v-btn
          v-bind="attrs"
          v-on="on"
          :small="smallText"
          block
          class=""
          depressed
          :color="buttonColor"
          :style="buttonStyle"
          :outlined="outlined"
          :disabled="disabled"
          :text="textTrigger"
        >
          <v-img
            v-if="image"
            :src="imgSrc"
            max-width="35"
            max-height="20"
            contain
          ></v-img>
          <v-icon class="mr-3" v-if="icon && !image">{{ icon }}</v-icon>
          <span v-if="editTriggerTitle && valuesHaveBeenSet">{{
            editTriggerTitle
          }}</span>
          <span v-else>{{ triggerTitle }}</span>
          <v-icon v-if="editIcon" x-small class="ml-2">mdi-pencil</v-icon>
        </v-btn>
        <div v-if="clearable">
          <v-slide-x-transition>
            <v-btn
              v-if="valuesHaveBeenSet"
              x-small
              depressed
              text
              color="red"
              class="ml-2"
              @click="resetAll"
              ><v-icon x-small color="red" class="mr-2">mdi-close-circle</v-icon
              >Clear Rx
            </v-btn>
          </v-slide-x-transition>
        </div>
      </div>
      <!-- <rx-summary-card-pair></rx-summary-card-pair> -->
    </template>

    <v-card cols="12" sm="6" md="4">
      <div class="cardHeader pa-4">
        <div class="d-flex flex-row justify-space-between">
          <div class="d-flex flex-column">
            <div class="d-flex flex-column flex-sm-row align-center">
              <div class="text-h4 pr-8">{{ this.title }}</div>

              <div
                class="d-flex flex-row align-end"
                v-if="showVertex && rxTypeLocal != 'cl'"
              >
                <div class="caption">Vertex dist.(mm):</div>

                <v-text-field
                  v-model.number="vertexDistanceInputVal"
                  type="number"
                  min="8"
                  max="16"
                  dense
                  hide-details="auto"
                  single-line
                  class="ml-2 compact-select"
                ></v-text-field>
              </div>
            </div>
            <div v-if="rxType == 'choose'">
              <v-radio-group
                v-model="rxTypeChoice"
                :row="$vuetify.breakpoint.smAndUp"
                hint="(choosing 'refraction' will vertex Rx before searching)"
                :persistent-hint="true"
              >
                <v-radio
                  value="refraction"
                  label="Search by refraction"
                ></v-radio>
                <v-radio value="cl" label="Search for CL Power"></v-radio>
              </v-radio-group>
            </div>
          </div>

          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
      </div>
      <!-- <v-card-actions> </v-card-actions> -->

      <v-card-text class="">
        <div class="caption py-2" v-if="additionalMessage">
          {{ additionalMessage }}
        </div>
        <v-container>
          <v-row justify="center" :align="'center'" class="" dense>
            <v-col cols="10" md="6">
              <rx-input
                eye="od"
                :rxString="rxString.od"
                @rxChange="setRxObjFromInput($event, 'od')"
                :showVertexed="rxTypeLocal == 'refraction'"
                :vertexDistance="vertexDistance"
                :add="parseFloat(addString)"
                :autofocus="true"
                :showOpticalCross="true"
                :showEyepiece="showEyepiece"
              />
            </v-col>
            <v-col cols="10" md="6">
              <rx-input
                eye="os"
                :rxString="rxString.os"
                @rxChange="setRxObjFromInput($event, 'os')"
                :showVertexed="rxTypeLocal == 'refraction'"
                :vertexDistance="vertexDistance"
                :add="parseFloat(addString)"
                :showOpticalCross="true"
                :showEyepiece="showEyepiece"
              />
            </v-col>
          </v-row>
          <v-row justify="center" :align="'center'" class="" v-show="showAdd">
            <v-col cols="6" md="4" class="d-flex justify-md-end">
              <div class="my-0 addField">
                <v-combobox
                  :items="addOptions"
                  label="Add"
                  rounded
                  dense
                  outlined
                  filled
                  clearable
                  background-color="#FFF"
                  :disabled="loading"
                  v-model="addString"
                  @change="handleAddChange"
                ></v-combobox>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="d-flex flex-row justify-center">
              <div class="d-inline-block mt-1">Dominant</div>
              <v-radio-group
                v-model="dominantEye"
                row
                class="d-inline-block ml-3 my-0"
              >
                <v-radio label="OD" value="od"></v-radio>
                <v-radio label="OS" value="os"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <v-dialog
            v-model="showPaste"
            scrollable
            :max-width="$vuetify.breakpoint.smAndUp ? '500' : '90%'"
            @keydown.esc="dialog = false"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                small
                v-bind="attrs"
                v-on="on"
                depressed
                color="green lighten-3"
                :block="!$vuetify.breakpoint.smAndUp"
                class="mt-4"
                tabindex="-1"
              >
                <v-icon small class="my-2">mdi-content-paste</v-icon>Or, Paste
                Rx</v-btn
              >
            </template>

            <v-card color="white">
              <v-card-text>
                <v-fade-transition>
                  <div v-if="!rxAlternatives">
                    <div class="my-2 title">
                      Copy and paste (or free type) your refraction here.
                    </div>
                    <div v-if="noParseResults" class="caption my-1 text--red">
                      {{ noParseResults }}
                    </div>

                    <v-textarea
                      class="my-3 mx-1"
                      name="rxText"
                      label="Enter or paste your powers"
                      :placeholder="'-2-1.25 x 180 \n-1.25-2x005 \n+2 add'"
                      autofocus
                      depressed
                      clearable
                      outlined
                      :loading="loading"
                      background-color="#edf7ff"
                      rows="5"
                      row-height="25"
                      @focus="$event.target.select()"
                      v-model="rxText"
                      :disabled="loading"
                    ></v-textarea>
                  </div>
                  <div v-else-if="rxAlternatives && rxAlternatives.length">
                    <!-- if text is parsed and multiple possible Rxs are returned -->
                    <div class="my-2 text-h4">Result</div>
                    <div v-if="!showAlternativeRxs">
                      <div class="my-2">Does this look correct?</div>
                      <v-card class="ma-2 pa-2" color="#fff5e6" outlined>
                        <rx-formatted
                          :rxPair="rxAlternatives[0]"
                        ></rx-formatted>
                      </v-card>
                      <div class="text-right my-4">
                        <v-btn
                          small
                          depressed
                          class="mr-2"
                          @click="showAlternativeRxs = true"
                          >No, that's not it...</v-btn
                        >
                        <v-btn
                          @click="setRxsFromTextAPI(rxAlternatives[0])"
                          small
                          depressed
                          color="primary"
                          >Yes</v-btn
                        >
                      </div>
                    </div>
                    <div v-else>
                      OK, how about these? <br />Click the correct Rx.
                      <div
                        v-for="(pair, i) in rxAlternatives"
                        :key="i"
                        class="d-flex flex-row flex-wrap pointer"
                      >
                        <v-card
                          hover
                          class="pointer ma-2 pa-2"
                          color="#fff5e6"
                          outlined
                          @click="setRxsFromTextAPI(pair)"
                        >
                          <rx-formatted :rxPair="pair"></rx-formatted>
                        </v-card>
                      </div>
                      <div class="my-2">
                        All of these are wrong? : Please
                        <span class="linkLike" @click="rxAlternatives = null"
                          >go back</span
                        >
                        and rewrite/reformat your Rx in a way a dumb computer
                        might understand.
                      </div>
                    </div>
                  </div>
                </v-fade-transition>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  @click="
                    showPaste = false;
                    rxAlternatives = null;
                  "
                  tabindex="-1"
                  small
                  depressed
                  >Cancel</v-btn
                >
                <v-btn
                  small
                  cols="2"
                  class="mx-2 my-md-8 d-inline d-md-block"
                  depressed
                  @click="rxToParseSubmitted()"
                  color="primary"
                  v-if="!rxAlternatives"
                  >Parse</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-container>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn depressed small @click="dialog = false">Cancel</v-btn>
        <v-btn
          depressed
          color="primary"
          small
          :disabled="loading"
          @click="closeAndEmitRx"
          >Use this Rx</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import RxMethods from "@/assets/js/cls/RxMethods";
import RxFormatted from "@/components/clStuff/RxFormatted";
import HashParserMixin from "@/assets/js/HashParserMixin";
import { mapState } from "vuex";

//import RxInput from "./RxInput";

export default {
  name: "RxParser",
  components: {
    RxInput: () => import("@/components/clStuff/RxInput"),
    RxFormatted,
    // RxSummaryCardPair: () => import("./RxSummaryCardPair"),
  },
  mixins: [RxMethods, HashParserMixin],
  props: {
    triggerTitle: {
      type: String,
      default: "Enter Rx",
    },
    textTrigger: null,
    smallText: {
      type: Boolean,
      default: true,
    },
    icon: { type: String, default: "mdi-glasses" },
    image: null,
    editIcon: null, // when true trigger is a small pencil and the word "edit"
    editTriggerTitle: null,
    inactiveMode: {
      type: String,
      default: "button", // "button" || "summary"
    },
    // if wanting to update an RxObject in the store.
    // should be the string name of an action, (usually "setRx")
    dispatchToStore: null,
    clearable: null,
    showAdd: {
      type: Boolean,
      default: true,
    },
    showVertex: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "Enter Rx",
    },
    disabled: null,
    goToPage: null, // a new rx will route to the specified page, appending the rx hash
    // color: {
    //   type: String,
    //   default: "white",
    // },
    rxType: {
      type: String,
      default: "choose", // or "cl" or "choose" or "refraction"
    },
    additionalMessage: null,
    buttonColor: null,
    buttonStyle: null,
    outlined: null,
    initialRxObjects: null,
    canOpenFromMenu: null,
    showEyepiece: null, // show an eyepiece instead of a lens on the optical crosses
  },
  data() {
    return {
      dialog: false,
      parsed: false,
      error: null,
      noParseResults: null,
      showAlternativeRxs: false,
      rxText: "",
      rxString: { od: "", os: "" },
      rxObjects: { od: {}, os: {} },
      addString: "",
      loading: false,
      vertexDistanceInputVal: 12,
      rxTypeChoice: "refraction",
      // vdInput: 12,
      vertexDistance: 0.012,
      dominantEye: "od",
      showPaste: false,
      addOptions: [
        "+1.00",
        "+1.25",
        "+1.50",
        "+1.75",
        "+2.00",
        "+2.25",
        "+2.50",
        "+2.75",
      ],
      rxAlternatives: null, // after parsing an Rx there may be several alternatives
      // vertexOptions: ["8", "9", "10", "11", "12", "13", "14", "15"],
    };
  },
  computed: {
    ...mapState(["showRxSearch"]),

    valuesHaveBeenSet: function () {
      return (
        this.rxObjects.od || this.rxObjects.os || parseFloat(this.addString) > 0
      );
    },
    hasAdd: function () {
      return parseInt(this.addString) > 0;
    },
    imgSrc: function () {
      if (!this.image) return null;
      if (this.image == "phoropter")
        return "https://www.eyedock.com/images/menu_icons/phoropter_icon.svg";
      else return this.image; // assume it's a file path to an image
    },
    rxTypeLocal: function () {
      // console.log(this.rxType);
      let rxType = this.rxType;
      let rtn = this.rxTypeChoice;
      if (rxType != "choose") rtn = rxType;
      return rtn;
    },
  },
  methods: {
    rxToParseSubmitted() {
      if (!this.rxText || this.rxText == "") return;
      this.showResults = false;
      // console.log(this.rxText);
      // clear the fields
      this.rxString.od = this.rxString.os = this.addString = "";
      this.loading = true;
      this.noParseResults = null;
      this.rxAlternatives = null;
      this.showAlternativeRxs = false;
      let url = "https://www.eyedock.com/bcse/api/parseRx2?rxText=";
      url += encodeURIComponent(this.rxText);

      // todo- use API services
      axios.get(url).then((response) => {
        // console.log(response.data);
        this.loading = false;
        let result = response.data;
        // console.log(result);
        if (result.error) {
          console.log(result.error);
        } else if (result.rxs) {
          let count = result.rxs.length;
          if (count === 1) this.setRxsFromTextAPI(result.rxs[0]);
          if (count > 1) this.rxAlternatives = result.rxs;
        } else {
          // todo - report no results on Rx parser?
          this.noParseResults =
            "Sorry, I wasn't able to parse this Rx. Please rewrite/reformat it and try again.";
        }
      });
      // todo report errors, stop spinner
      // console.log(this.RxClass.test());
    },

    setRxsFromTextAPI(rxs) {
      this.rxString.od = this.assignValueFromResult(rxs, "od", "string");
      this.rxString.os = this.assignValueFromResult(rxs, "os", "string");
      this.rxObjects.od = this.parseRxString(this.rxString.od);
      this.rxObjects.os = this.parseRxString(this.rxString.os);
      this.addString = this.assignValueFromResult(rxs, "od", "add");
      this.parsed = true;
      this.showResults = true;
      this.showPaste = false;
      this.rxAlternatives = null;
    },

    closeAndEmitRx() {
      let hasAdd = this.addString != "" && !isNaN(this.addString);
      let addString = hasAdd ? parseFloat(this.addString) : null;
      // if (!this.valuesHaveBeenSet) {
      //   alert("Please enter an Rx or cancel");
      //   return;
      // }
      ["od", "os"].forEach((eye) => {
        // add the add power and vertex to the object
        if (this.rxObjects[eye]) {
          this.rxObjects[eye].add = addString;
          if (this.rxTypeLocal == "cl") {
            this.rxObjects[eye].vertex = 0;
          } else {
            this.rxObjects[eye].vertex = this.vertexDistance;
          }
          this.rxObjects[eye].dominant = this.dominantEye == eye;
        }
      });
      // todo do some final error checking
      // update the hash
      let rxHash = this.rxHashOU(this.rxObjects);
      this.$emit("rxHash", rxHash);
      this.$emit("rxChange", this.rxObjects);
      if (this.dispatchToStore) this.updateStore(rxHash);
      if (this.goToPage) {
        // console.log(rxHash);
        // if (this.rxTypeLocal == "cl") rxHash += "&searchForPower=1";
        this.routeToPage(rxHash);
      }
      this.dialog = false;
    },
    routeToPage(rxHash) {
      // rxHash = encodeURI(rxHash; // already encodedby rxHash function
      let path = `${this.goToPage}#` + rxHash;
      this.$router.push({ path: path });
    },
    assignValueFromResult(result, eyeIndex, key) {
      if (typeof result[eyeIndex] === "undefined") return "";
      if (typeof result[eyeIndex][key] === "undefined") return "";
      return result[eyeIndex][key];
    },
    rxElementStyle(item) {
      return `left:${item.x}px; top:${item.y}px`;
    },
    setRxObjFromInput(rxObj, eye) {
      // console.log(rxObj);
      this.rxObjects[eye] = rxObj;
      this.rxString[eye] = this.prettyString(rxObj); //rxObj.prettyString();
      // todo - error check
    },

    handleAddChange() {
      let add = this.addString;
      this.addString = this.numToDiopterString(add, 1);
    },
    handleVertexChange() {
      let input = this.vertexDistanceInputVal;
      if (isNaN(input)) {
        this.vertexDistanceInputVal = this.vertexDistance * 1000;
        return;
      }
      while (input < 5) input *= 10;
      //console.log("vertex input: " + input);
      if (input > 25 || input < 7) {
        this.vertexDistanceInputVal = this.vertexDistance * 1000;
        // todo - show a message
      } else {
        this.vertexDistanceInputVal = input;
        this.vertexDistance = input / 1000;
      }
    },
    resetAll() {
      (this.rxString = { od: "", os: "" }),
        (this.rxObjects = { od: {}, os: {} }),
        (this.addString = "");
      this.rxText = "";
      this.closeAndEmitRx();
    },
    // will only run if dispatchToStore is set
    updateStore() {
      if (!this.dispatchToStore) return; // redundant
      if (this.rxObjects.od)
        this.$store.dispatch(this.dispatchToStore, {
          eye: "od",
          rx: this.rxObjects.od,
        });
      if (this.rxObjects.os)
        this.$store.dispatch(this.dispatchToStore, {
          eye: "os",
          rx: this.rxObjects.os,
        });
    },
    setRxsFromInitial(initial) {
      this.rxObjects = JSON.parse(JSON.stringify(initial)); // hacky way to clone object
      for (const eye of ["od", "os"]) {
        if (initial[eye]) {
          this.rxString[eye] = this.prettyString(initial[eye]);
          if (initial[eye].add) this.addString = this.prettyAdd(initial[eye]);
          if (initial[eye].dominant) this.dominantEye = eye;
          if (initial[eye].vertex)
            this.vertexDistanceInputVal = initial[eye].vertex * 1000;
          // if vertex=0 assume the given Rx is a CL Rx
          if (initial[eye].vertex === 0 && this.rxType == "choose")
            this.rxTypeChoice = "cl";
        } else this.rxString[eye] = null;
      }
    },
    resetDialogVars() {
      // console.log("resetting parser dialog vars");
      // if this dialog was opened via setting the store showRxSearch variable (from the menu)
      this.$store.dispatch("showRxSearch", false);
      this.removeFromHash("inputRx"); // from hashParserMixin.js
    },
    // setFocusName() {
    //   this.$refs.firstElement.focus();
    // },
  },
  watch: {
    initialRxObjects: {
      handler(val) {
        this.setRxsFromInitial(val);
      },
      deep: true,
    },
    showRxSearch(val) {
      // console.log("showRxSearch", val);
      if (val && this.canOpenFromMenu) {
        this.dialog = true;
        this.resetDialogVars();
      }
    },
    dialog(val) {
      if (val) {
        // setTimeout(() => {
        //   this.setFocusName();
        // });
        this.resetDialogVars();
      }
    },
  },
  mounted() {
    if (this.initialRxObjects) this.setRxsFromInitial(this.initialRxObjects);
    let openDialog = this.hashValForKey("inputRx");
    if (openDialog || this.showRxSearch) {
      this.dialog = true;
      this.resetDialogVars();
    }
  },
};
</script>

<style scoped>
.textareaSize {
  width: 400px;
  max-width: 90%;
}
.rxCell {
  margin-right: 2em;
  display: inline-block;
  white-space: nowrap;
}
/* .vDistClass {
  max-width: 50px;
} */
.pointer {
  cursor: pointer;
}
.fullWidth {
  width: 100%;
}
.addField {
  width: 160px;
}
.cardHeader {
  background-color: #bde0ff;
}
</style>
